import { supabase } from "../lib/supabase";

export type HotelNameMap = Record<string, string>;

function resolveHotelName(row: {
  hotel_code?: string | null;
  company_name?: string | null;
  hotel_name?: string | null;
}): string | null {
  if (!row.hotel_code) {
    return null;
  }

  const name = (row.company_name ?? row.hotel_name ?? "").trim();
  return name || row.hotel_code;
}

async function fetchFromHotelDetails(hotelCodes: string[]): Promise<HotelNameMap> {
  const nextMap: HotelNameMap = {};

  const primary = await supabase
    .from("hotel_details")
    .select("hotel_code, company_name, hotel_name")
    .in("hotel_code", hotelCodes);

  if (!primary.error && primary.data) {
    primary.data.forEach(row => {
      const name = resolveHotelName(row);
      if (name && row.hotel_code) {
        nextMap[row.hotel_code] = name;
      }
    });
    return nextMap;
  }

  if (primary.error?.message?.includes("company_name")) {
    const fallback = await supabase
      .from("hotel_details")
      .select("hotel_code, hotel_name")
      .in("hotel_code", hotelCodes);

    if (!fallback.error && fallback.data) {
      fallback.data.forEach(row => {
        const name = resolveHotelName(row);
        if (name && row.hotel_code) {
          nextMap[row.hotel_code] = name;
        }
      });
    }
  } else if (primary.error) {
    console.error("Error loading hotel names from hotel_details:", primary.error);
  }

  return nextMap;
}

async function fetchFromHotelAccountsSecure(
  hotelCodes: string[],
  existing: HotelNameMap,
): Promise<HotelNameMap> {
  const missingCodes = hotelCodes.filter(code => !existing[code] || existing[code] === code);
  if (!missingCodes.length) {
    return existing;
  }

  const nextMap: HotelNameMap = { ...existing };

  const { data, error } = await supabase
    .from("hotel_accounts_secure")
    .select("hotel_code, company_name, hotel_name")
    .in("hotel_code", missingCodes);

  if (error) {
    console.error("Error loading hotel names from hotel_accounts_secure:", error);
    return nextMap;
  }

  (data || []).forEach(row => {
    const name = resolveHotelName(row);
    if (name && row.hotel_code) {
      nextMap[row.hotel_code] = name;
    }
  });

  return nextMap;
}

export async function fetchHotelNameMap(hotelCodes: string[]): Promise<HotelNameMap> {
  const uniqueCodes = [...new Set(hotelCodes.filter(Boolean))];
  if (!uniqueCodes.length) {
    return {};
  }

  try {
    const fromDetails = await fetchFromHotelDetails(uniqueCodes);
    return fetchFromHotelAccountsSecure(uniqueCodes, fromDetails);
  } catch (err) {
    console.error("Error loading hotel names:", err);
    return {};
  }
}

export function getHotelDisplayName(hotelCode: string, hotelNames: HotelNameMap): string {
  return hotelNames[hotelCode]?.trim() || hotelCode;
}

export function formatHotelCodeList(hotelCodes: string[], hotelNames: HotelNameMap): string {
  if (!hotelCodes.length) {
    return "";
  }

  return [...new Set(hotelCodes)]
    .map(code => getHotelDisplayName(code, hotelNames))
    .join(", ");
}

export function mapBreakdownKeysToHotelNames<
  T extends { key: string; count: number; revenue: number },
>(rows: T[], hotelNames: HotelNameMap): Array<T & { label: string }> {
  return rows.map(row => ({
    ...row,
    label: getHotelDisplayName(row.key, hotelNames),
  }));
}
